import { useState } from 'react';
import useInView from '../hooks/useInView';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactSuccess = ({ t, onReset }) => (
  <div data-testid="contact-success" className="h-full flex flex-col justify-center py-16 text-center">
    <div className="line-gold mb-6 mx-auto" />
    <h3 className="font-heading font-light text-obsidian mb-4" style={{ fontSize: '2rem' }}>
      {t.successHeading}
    </h3>
    <p className="font-body text-winery-text text-base leading-relaxed max-w-sm mx-auto">
      {t.successBody}
    </p>
    <button
      data-testid="contact-reset"
      onClick={onReset}
      className="mt-8 font-body text-xs tracking-widest uppercase text-winery-gold hover:text-obsidian transition-colors duration-300 mx-auto"
    >
      {t.successReset}
    </button>
  </div>
);

const ContactForm = ({ t, form, status, onChange, onSubmit }) => (
  <form data-testid="contact-form" onSubmit={onSubmit} className="space-y-8">
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
      <div>
        <label htmlFor="name" className="section-label block mb-3">{t.nameLabel}</label>
        <input
          id="name" name="name" type="text" required
          data-testid="contact-name"
          value={form.name} onChange={onChange}
          placeholder={t.namePlaceholder}
          className="input-underline w-full"
        />
      </div>
      <div>
        <label htmlFor="email" className="section-label block mb-3">{t.emailInputLabel}</label>
        <input
          id="email" name="email" type="email" required
          data-testid="contact-email-input"
          value={form.email} onChange={onChange}
          placeholder={t.emailPlaceholder}
          className="input-underline w-full"
        />
      </div>
    </div>

    <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
      <div>
        <label htmlFor="phone" className="section-label block mb-3">{t.phoneInputLabel}</label>
        <input
          id="phone" name="phone" type="tel"
          data-testid="contact-phone-input"
          value={form.phone} onChange={onChange}
          placeholder={t.phonePlaceholder}
          className="input-underline w-full"
        />
      </div>
      <div>
        <label htmlFor="reason" className="section-label block mb-3">{t.reasonLabel}</label>
        <select
          id="reason" name="reason" required
          data-testid="contact-reason"
          value={form.reason} onChange={onChange}
          className="input-underline w-full bg-transparent cursor-pointer"
        >
          <option value="" disabled>{t.reasonPlaceholder}</option>
          {t.reasons.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
      </div>
    </div>

    <div>
      <label htmlFor="message" className="section-label block mb-3">{t.messageLabel}</label>
      <textarea
        id="message" name="message" required rows={5}
        data-testid="contact-message"
        value={form.message} onChange={onChange}
        placeholder={t.messagePlaceholder}
        className="input-underline w-full resize-none"
        style={{ borderBottom: '1px solid rgba(26,26,26,0.2)' }}
      />
    </div>

    <div className="flex items-center gap-6">
      <button
        type="submit"
        data-testid="contact-submit"
        disabled={status === 'loading'}
        className="font-body text-xs tracking-widest uppercase px-10 py-4 bg-obsidian text-cream hover:bg-obsidian/80 transition-all duration-300 disabled:opacity-50"
      >
        {status === 'loading' ? t.submitLoading : t.submitIdle}
      </button>
      {status === 'error' && (
        <p data-testid="contact-error" className="font-body text-xs text-red-500">
          {t.errorMsg}
        </p>
      )}
    </div>
  </form>
);

const EMPTY_FORM = { name: '', email: '', phone: '', reason: '', message: '' };

const Contact = () => {
  const [ref, inView] = useInView(0.06);
  const { t } = useLanguage();
  const [form, setForm] = useState(EMPTY_FORM);
  const [status, setStatus] = useState('idle');

  const handleChange = (e) => setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.reason || !form.message) return;
    setStatus('loading');
    try {
      await axios.post(`${API}/contact`, form);
      setStatus('success');
      setForm(EMPTY_FORM);
    } catch {
      setStatus('error');
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="bg-winery-muted py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12" ref={ref}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-24">

          <div className="lg:col-span-5">
            <div className={`reveal ${inView ? 'visible' : ''}`}>
              <span className="section-label">{t.contact.label}</span>
              <div className="line-gold mt-4 mb-8" />
            </div>
            <p className={`reveal reveal-delay-1 font-script text-3xl md:text-4xl text-winery-gold mb-6 ${inView ? 'visible' : ''}`}>
              {t.contact.scriptHeading}
            </p>
            <h2
              className={`reveal reveal-delay-2 font-heading font-light text-obsidian leading-tight mb-7 ${inView ? 'visible' : ''}`}
              style={{ fontSize: 'clamp(1.8rem, 3.5vw, 2.8rem)' }}
            >
              {t.contact.heading}
            </h2>
            <p className={`reveal reveal-delay-3 font-body text-winery-text text-base leading-relaxed mb-10 ${inView ? 'visible' : ''}`}>
              {t.contact.body}
            </p>
            <div className={`reveal reveal-delay-4 space-y-4 ${inView ? 'visible' : ''}`}>
              <div>
                <p className="font-body text-xs tracking-widest uppercase text-winery-gold mb-1">{t.contact.emailLabel}</p>
                <a href="mailto:gospodinovioan@gmail.com" data-testid="contact-email"
                  className="font-body text-sm text-obsidian hover:text-winery-gold transition-colors duration-300">
                  gospodinovioan@gmail.com
                </a>
              </div>
              <div>
                <p className="font-body text-xs tracking-widest uppercase text-winery-gold mb-1">{t.contact.phoneLabel}</p>
                <a href="tel:+359884699193" data-testid="contact-phone"
                  className="font-body text-sm text-obsidian hover:text-winery-gold transition-colors duration-300">
                  +359 884 699 193
                </a>
              </div>
              <div>
                <p className="font-body text-xs tracking-widest uppercase text-winery-gold mb-1">{t.contact.locationLabel}</p>
                <p className="font-body text-sm text-winery-text">{t.contact.locationValue}</p>
              </div>
            </div>
          </div>

          <div className={`reveal reveal-delay-2 lg:col-span-7 ${inView ? 'visible' : ''}`}>
            {status === 'success'
              ? <ContactSuccess t={t.contact} onReset={() => setStatus('idle')} />
              : <ContactForm t={t.contact} form={form} status={status} onChange={handleChange} onSubmit={handleSubmit} />
            }
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
